import { Themes, Theme, getThemeByName, DEFAULT_THEME_NAME } from "../data/Themes";

/**
 * Applies the selected theme app-wide by overriding the global CSS custom
 * properties that drive the brand accent colour. Every component that reads
 * --primaryColor / --gold / --primary-color (index.css + cstheme.css) recolours
 * instantly, so this is the single place that makes a theme apply "for whole app".
 */
export const applyTheme = (themeName: string) => {
  const theme = Themes.find((t) => t.name === themeName) || Themes[0];
  const root = document.documentElement;

  // Brand accent (formerly the static gold #B8A07E across the app)
  root.style.setProperty("--primaryColor", theme.primaryColor);
  root.style.setProperty("--gold", theme.primaryColor);
  root.style.setProperty("--highlight-text-color", theme.primaryColor);
  root.style.setProperty("--bg-primary", theme.primaryColor, "important");
  // Subtle themed tint for focus rings / hover glows (primary @ ~20% alpha)
  root.style.setProperty("--boxShadow", `${theme.primaryColor}33`);

  // PrimeReact accent (buttons, highlights, active states)
  root.style.setProperty("--primary-color", theme.primaryColor);
  root.style.setProperty("--primary-color-text", theme.fontColor || "#ffffff");
};

/**
 * Applies the last-saved theme's brand accent on public pages (login, register,
 * …) that render outside MainLayout and therefore never run getUserTheme().
 *
 * It reads the saved theme name from localStorage (falling back to the plain
 * "Theme" key written straight after login, then the organisation default) and
 * repaints the brand CSS variables via applyTheme(). It deliberately forces the
 * light splash by clearing any lingering `darkTheme`/`dark` body classes left
 * behind by a previous signed-in session, so these pages keep their intended
 * light design while still honouring the chosen accent colour.
 */
/**
 * Resolves (without applying) the last-saved theme from localStorage, so public
 * pages can branch on its mode/colour before painting. Reads `Theme-Name` first,
 * then the plain `Theme` key written straight after login, then the org default.
 */
export const resolveStoredTheme = (): Theme => {
  const stored = localStorage.getItem("Theme-Name");
  let name: string | null = null;
  if (stored) {
    try {
      name = JSON.parse(stored).themeName;
    } catch {
      /* corrupt value — fall through to the other sources */
    }
  }
  if (!name) name = localStorage.getItem("Theme");
  return getThemeByName(name || DEFAULT_THEME_NAME);
};

export const bootstrapThemeFromStorage = (): Theme => {
  const theme = resolveStoredTheme();
  applyTheme(theme.name);
  document.body.classList.remove("darkTheme", "dark");
  return theme;
};
